import { useEasyMode } from "@/contexts/EasyModeContext";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Global keyboard shortcuts when Easy Mode is enabled.
 * Handles Esc to go back (when no dialog/modal is open), and focus management for Tab navigation.
 */
export function EasyModeKeyboardHints() {
  const { isEasyMode, showCommandPalette, activeModal } = useEasyMode();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEasyMode) return;

    const handler = (e: KeyboardEvent) => {
      // Don't intercept if command palette or a transaction modal is open
      if (showCommandPalette || activeModal) return;

      const target = (e.composedPath ? e.composedPath()[0] : e.target) as HTMLElement | null;
      const tagName = target?.tagName ? target.tagName.toUpperCase() : "";
      const isInput =
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        Boolean(target?.isContentEditable) ||
        Boolean(target?.closest?.('input, textarea, select, [contenteditable="true"]'));

      // If any modal/dialog is open, do not go back (let dialog handle Esc)
      const hasOpenDialog = Boolean(document.querySelector('[role="dialog"], [data-state="open"]'));
      if (hasOpenDialog) return;

      // Esc → go back (only when not in an input and no modal is active)
      if (e.key === "Escape" && !isInput) {
        e.preventDefault();
        navigate(-1);
        return;
      }

      // Alt+number shortcuts for quick navigation
      if (e.altKey && e.key >= "1" && e.key <= "9") {
        // Let browser handle these naturally - could be extended for specific pages
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isEasyMode, showCommandPalette, activeModal, navigate]);

  // Add tabindex to main content area for better tab flow
  useEffect(() => {
    if (!isEasyMode) return;

    // Enhance all interactive elements with visible focus styles
    document.body.classList.add("easy-mode-active");
    return () => document.body.classList.remove("easy-mode-active");
  }, [isEasyMode]);

  return null;
}
